import type { ConditionModel } from '@ctw/shared/api/fhir/models/condition';
import { CodingList } from '@ctw/shared/components/coding-list';
import { ContentCard } from '@ctw/shared/components/containers/content-card';
import { DefinitionList } from '@ctw/shared/components/definition-list';
import { ResponsiveSourceDocumentLink } from '@ctw/shared/content/CCDA/responsive-source-document-link';
import { useConditionHistory } from '@ctw/shared/content/conditions/history';
import { History } from '@ctw/shared/content/resource/history';
import type { UseResourceDrawerOpenDrawerArgs } from '@ctw/shared/context/resource-drawer-context';
import { tw } from '@ctw/shared/utils/tailwind';
import { capitalize } from 'lodash-es';

const conditionHistory = ({ model }: { model: ConditionModel }) => (
  <History getHistory={useConditionHistory} model={model} />
);

export const conditionDrawerAction: UseResourceDrawerOpenDrawerArgs<'Condition'>['action'] = ({
  model,
  requestContext,
}) => {
  let label: string;
  if (model.isSummaryResource) {
    label = 'Condition';
  } else {
    label = model.isEncounterDiagnosis ? 'Encounter Diagnosis' : 'Condition';
  }

  return {
    label,
    title: `${capitalize(model.display)}`,
    content: {
      body: (
        <div className={tw`flex flex-col gap-5`}>
          <ContentCard
            variant="focal"
            title="Details"
            content={{
              header: <ResponsiveSourceDocumentLink model={model} />,
            }}
          >
            <DefinitionList
              items={
                model.isSummaryResource
                  ? lensConditionDefinitionList(model)
                  : conditionDefinitionList(model, requestContext.builderId)
              }
            />
          </ContentCard>
          {model.isSummaryResource && conditionHistory({ model })}
        </div>
      ),
    },
  };
};

const lensConditionDefinitionList = (model: ConditionModel) => [
  { label: 'Latest Diagnosis', value: model.recordedDate },
  { label: 'Recorder', value: model.recorder },
  {
    label: 'Provider Organization',
    value: model.patientOrganizationName,
  },
  { label: 'Onset Date', value: model.onset },
  { label: 'Type', value: capitalize(model.type) },
  { label: 'Status', value: capitalize(model.displayStatus) },
  { label: 'Abatement Date', value: model.abatement },
  {
    label: 'ICD-10',
    value:
      model.icd10CMEnrichments.length > 0 ? (
        <CodingList codings={model.icd10CMEnrichments} />
      ) : null,
  },
  {
    label: 'HCC',
    value: model.v28HCCCodes.length > 0 ? <CodingList codings={model.v28HCCCodes} /> : null,
  },
  {
    label: 'Note',
    value: model.notes.length > 0 && (
      <div className={tw`flex flex-col space-y-4`}>
        {model.notes.map((note) => (
          <div key={note}>
            <div>{note}</div>
          </div>
        ))}
      </div>
    ),
  },
];

const conditionDefinitionList = (model: ConditionModel, builderId: string) => [
  { label: 'Recorded', value: model.recordedDate },
  { label: 'Recorder', value: model.recorder },
  {
    label: 'Provider Organization',
    value: model.patientOrganizationName,
  },
  {
    label: 'ICD-10',
    value:
      model.icd10CMEnrichments.length > 0 ? (
        <CodingList codings={model.icd10CMEnrichments} />
      ) : null,
  },
  {
    label: 'HCC',
    value: model.v28HCCCodes.length > 0 ? <CodingList codings={model.v28HCCCodes} /> : null,
  },
  { label: 'Data Source', value: model.getDataSourceDisplay(builderId) },
];
